// Pure, recursive serializer: ConditionNode (UI model) -> triggers.Condition (proto).
// Numeric thresholds stay decimal STRINGS; *_percent fields are percentage points;
// *_ms / period / timeframe are parsed per the proto field types. Unit-tested in
// __tests__/trigger-condition-builder.test.ts.
import { Exchange, OptionType, PriceReference, TradeVenue } from '@/types/generated/common/types'
import {
  ActionEventType,
  CandleField,
  ComparisonOperator,
  type Condition,
  OptionIvSource,
  PortfolioMetric,
  PositionMetric,
  PriceOffsetUnit,
  RiskMetric,
  type TechnicalIndicatorSpec,
  TechnicalIndicatorType,
  type TriggerInstrumentRef,
  type TriggerOptionInstrumentRef,
} from '@/types/generated/triggers'

import type {
  ActionEventTypeToken,
  CandleFieldToken,
  ConditionNode,
  IndicatorSpecValue,
  OperatorToken,
  OptionIvSourceToken,
  OptionTypeToken,
  PositionMetricToken,
  PriceOffsetUnitToken,
  PriceSourceToken,
  RiskMetricToken,
} from './trigger-condition-model'

export interface BuildConditionContext {
  /** Exchange for instrument refs, derived from the active account. */
  exchange: Exchange
  /** Trade venue for instrument refs, derived from the active account. */
  tradeVenue: TradeVenue
  /** Symbol used when a leaf node leaves its symbol blank. */
  defaultSymbol: string
  /** Account used when an account-scoped leaf leaves its account blank. */
  defaultAccountId: string
}

const OPERATOR_MAP: Record<OperatorToken, ComparisonOperator> = {
  equal_to: ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
  not_equal: ComparisonOperator.COMPARISON_OPERATOR_NOT_EQUAL,
  greater_than: ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN,
  less_than: ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN,
  greater_than_or_equal: ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL,
  less_than_or_equal: ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL,
  cross_above: ComparisonOperator.COMPARISON_OPERATOR_CROSS_ABOVE,
  cross_below: ComparisonOperator.COMPARISON_OPERATOR_CROSS_BELOW,
}

const PRICE_SOURCE_MAP: Record<PriceSourceToken, PriceReference> = {
  mark: PriceReference.PRICE_REFERENCE_MARK_PRICE,
  last: PriceReference.PRICE_REFERENCE_LAST_PRICE,
  index: PriceReference.PRICE_REFERENCE_INDEX_PRICE,
  mid: PriceReference.PRICE_REFERENCE_MID_PRICE,
  best_bid: PriceReference.PRICE_REFERENCE_BEST_BID,
  best_ask: PriceReference.PRICE_REFERENCE_BEST_ASK,
}

const INDICATOR_TYPE_MAP: Record<IndicatorSpecValue['indicatorType'], TechnicalIndicatorType> = {
  sma: TechnicalIndicatorType.TECHNICAL_INDICATOR_TYPE_SMA,
  ema: TechnicalIndicatorType.TECHNICAL_INDICATOR_TYPE_EMA,
}

const CANDLE_FIELD_MAP: Record<CandleFieldToken, CandleField> = {
  open: CandleField.CANDLE_FIELD_OPEN,
  high: CandleField.CANDLE_FIELD_HIGH,
  low: CandleField.CANDLE_FIELD_LOW,
  close: CandleField.CANDLE_FIELD_CLOSE,
  volume_base: CandleField.CANDLE_FIELD_VOLUME_BASE,
  volume_quote: CandleField.CANDLE_FIELD_VOLUME_QUOTE,
  vwap: CandleField.CANDLE_FIELD_VWAP,
  twap: CandleField.CANDLE_FIELD_TWAP,
}

const POSITION_METRIC_MAP: Record<PositionMetricToken, PositionMetric> = {
  quantity: PositionMetric.POSITION_METRIC_QUANTITY,
  mark_price: PositionMetric.POSITION_METRIC_MARK_PRICE,
  unrealized_pnl: PositionMetric.POSITION_METRIC_UNREALIZED_PNL,
  realized_pnl: PositionMetric.POSITION_METRIC_REALIZED_PNL,
  daily_unrealized_pnl: PositionMetric.POSITION_METRIC_DAILY_UNREALIZED_PNL,
  initial_margin: PositionMetric.POSITION_METRIC_INITIAL_MARGIN,
  maintenance_margin: PositionMetric.POSITION_METRIC_MAINTENANCE_MARGIN,
  delta: PositionMetric.POSITION_METRIC_DELTA,
  gamma: PositionMetric.POSITION_METRIC_GAMMA,
  vega: PositionMetric.POSITION_METRIC_VEGA,
  theta: PositionMetric.POSITION_METRIC_THETA,
  rho: PositionMetric.POSITION_METRIC_RHO,
  delta_usd: PositionMetric.POSITION_METRIC_DELTA_USD,
  gamma_usd: PositionMetric.POSITION_METRIC_GAMMA_USD,
  vega_usd: PositionMetric.POSITION_METRIC_VEGA_USD,
  theta_usd: PositionMetric.POSITION_METRIC_THETA_USD,
  unrealized_pnl_usd: PositionMetric.POSITION_METRIC_UNREALIZED_PNL_USD,
  realized_pnl_usd: PositionMetric.POSITION_METRIC_REALIZED_PNL_USD,
  daily_unrealized_pnl_usd: PositionMetric.POSITION_METRIC_DAILY_UNREALIZED_PNL_USD,
  initial_margin_usd: PositionMetric.POSITION_METRIC_INITIAL_MARGIN_USD,
  maintenance_margin_usd: PositionMetric.POSITION_METRIC_MAINTENANCE_MARGIN_USD,
}

const RISK_METRIC_MAP: Record<RiskMetricToken, RiskMetric> = {
  pnl: RiskMetric.RISK_METRIC_PNL,
  delta: RiskMetric.RISK_METRIC_DELTA,
  gamma: RiskMetric.RISK_METRIC_GAMMA,
  vega: RiskMetric.RISK_METRIC_VEGA,
  theta: RiskMetric.RISK_METRIC_THETA,
  rho: RiskMetric.RISK_METRIC_RHO,
  vanna: RiskMetric.RISK_METRIC_VANNA,
  volga: RiskMetric.RISK_METRIC_VOLGA,
  weighted_vega: RiskMetric.RISK_METRIC_WEIGHTED_VEGA,
}

const OPTION_IV_SOURCE_MAP: Record<OptionIvSourceToken, OptionIvSource> = {
  bid_iv: OptionIvSource.OPTION_IV_SOURCE_BID_IV,
  ask_iv: OptionIvSource.OPTION_IV_SOURCE_ASK_IV,
  mark_iv: OptionIvSource.OPTION_IV_SOURCE_MARK_IV,
  mid_iv: OptionIvSource.OPTION_IV_SOURCE_MID_IV,
}

const OPTION_TYPE_MAP: Record<OptionTypeToken, OptionType> = {
  call: OptionType.OPTION_TYPE_CALL,
  put: OptionType.OPTION_TYPE_PUT,
}

const PRICE_OFFSET_UNIT_MAP: Record<PriceOffsetUnitToken, PriceOffsetUnit> = {
  quote_value: PriceOffsetUnit.PRICE_OFFSET_UNIT_QUOTE_VALUE,
  bps: PriceOffsetUnit.PRICE_OFFSET_UNIT_BPS,
  percent: PriceOffsetUnit.PRICE_OFFSET_UNIT_PERCENT,
  ticks: PriceOffsetUnit.PRICE_OFFSET_UNIT_TICKS,
}

const ACTION_EVENT_TYPE_MAP: Record<ActionEventTypeToken, ActionEventType> = {
  placed: ActionEventType.ACTION_EVENT_TYPE_PLACED,
  filled: ActionEventType.ACTION_EVENT_TYPE_FILLED,
  partially_filled: ActionEventType.ACTION_EVENT_TYPE_PARTIALLY_FILLED,
  cancelled: ActionEventType.ACTION_EVENT_TYPE_CANCELLED,
  failed: ActionEventType.ACTION_EVENT_TYPE_FAILED,
}

/** Normalize a numeric threshold to a decimal string: strip thousands separators, trim. */
function decimal(value: string): string {
  return value.replace(/,/g, '').trim()
}

/** Trim, drop empties, dedupe (preserve first-seen order) for db_news chip groups. */
function cleanChips(values: string[]): string[] {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))]
}

function instrumentRef(symbol: string, ctx: BuildConditionContext): TriggerInstrumentRef {
  return {
    symbol,
    exchange: ctx.exchange,
    tradeVenue: ctx.tradeVenue,
    venueSymbol: '',
    asset: undefined,
  }
}

function buildIndicatorSpec(
  spec: IndicatorSpecValue,
  ctx: BuildConditionContext
): TechnicalIndicatorSpec {
  return {
    indicatorType: INDICATOR_TYPE_MAP[spec.indicatorType],
    instrument: instrumentRef(spec.symbol || ctx.defaultSymbol, ctx),
    source: PRICE_SOURCE_MAP[spec.source],
    periodCount: Number(spec.periodCount),
    timeframeSeconds: Number(spec.timeframeSeconds),
  }
}

/** Build a single proto Condition (recursive for and/or nodes). */
export function buildCondition(node: ConditionNode, ctx: BuildConditionContext): Condition {
  switch (node.kind) {
    case 'price': {
      const condition: Condition['price'] = {
        instrument: instrumentRef(node.symbol || ctx.defaultSymbol, ctx),
        source: PRICE_SOURCE_MAP[node.source],
        operator: OPERATOR_MAP[node.op],
        value: decimal(node.value),
      }
      if (node.relative) {
        condition.relative = {
          anchor: PRICE_SOURCE_MAP[node.relative.anchor],
          offset: decimal(node.relative.offset),
          offsetUnit: PRICE_OFFSET_UNIT_MAP[node.relative.offsetUnit],
        }
      }
      return { price: condition }
    }

    case 'technical_indicator': {
      const condition: NonNullable<Condition['technicalIndicator']> = {
        left: buildIndicatorSpec(node.left, ctx),
        operator: OPERATOR_MAP[node.op],
      }
      if (node.targetMode === 'indicator') {
        condition.right = buildIndicatorSpec(node.right, ctx)
      } else {
        condition.threshold = decimal(node.threshold)
      }
      return { technicalIndicator: condition }
    }

    case 'returns':
      return {
        returns: {
          instrument: instrumentRef(node.symbol || ctx.defaultSymbol, ctx),
          source: PRICE_SOURCE_MAP[node.source],
          windowMs: decimal(node.windowMs),
          operator: OPERATOR_MAP[node.op],
          valuePercent: decimal(node.valuePercent),
          timeframeSeconds: Number(node.timeframeSeconds),
        },
      }

    case 'candle':
      return {
        candle: {
          instrument: instrumentRef(node.symbol || ctx.defaultSymbol, ctx),
          timeframeSeconds: Number(node.timeframeSeconds),
          field: CANDLE_FIELD_MAP[node.field],
          operator: OPERATOR_MAP[node.op],
          value: decimal(node.value),
          allowOpenCandle: node.allowOpenCandle,
        },
      }

    case 'portfolio_metric': {
      const accountId = node.accountId || ctx.defaultAccountId
      const operator = OPERATOR_MAP[node.op]
      const value = decimal(node.value)
      if (node.metric === 'account_value_usd') {
        return {
          portfolioMetric: {
            accountId,
            metric: PortfolioMetric.PORTFOLIO_METRIC_ACCOUNT_VALUE_USD,
            operator,
            value,
          },
        }
      }
      return {
        portfolioMetric: {
          accountId,
          metric:
            node.unit === 'usd'
              ? PortfolioMetric.PORTFOLIO_METRIC_CURRENCY_EQUITY_USD
              : PortfolioMetric.PORTFOLIO_METRIC_EQUITY,
          operator,
          value,
          currency: node.currency.trim().toUpperCase(),
        },
      }
    }

    case 'position_stats': {
      const scope: NonNullable<NonNullable<Condition['positionStats']>['scope']> =
        node.scopeKind === 'all'
          ? { allPositions: true }
          : node.scopeKind === 'symbol'
            ? { symbol: node.scopeValue || ctx.defaultSymbol }
            : node.scopeKind === 'underlying'
              ? { underlying: node.scopeValue }
              : { positionGroupId: node.scopeValue }
      return {
        positionStats: {
          accountId: node.accountId || ctx.defaultAccountId,
          scope,
          metric: POSITION_METRIC_MAP[node.metric],
          operator: OPERATOR_MAP[node.op],
          value: decimal(node.value),
        },
      }
    }

    case 'risk_metric':
      return {
        riskMetric: {
          accountId: node.accountId || ctx.defaultAccountId,
          asset: node.asset,
          metric: RISK_METRIC_MAP[node.metric],
          operator: OPERATOR_MAP[node.op],
          value: decimal(node.value),
        },
      }

    case 'option_iv': {
      const instrument: TriggerOptionInstrumentRef = {
        symbol: node.symbol || ctx.defaultSymbol,
        exchange: ctx.exchange,
        tradeVenue: ctx.tradeVenue,
        underlying: node.underlying,
      }
      if (node.strikePrice) instrument.strikePrice = decimal(node.strikePrice)
      if (node.expiryMs) instrument.expiryMs = node.expiryMs
      if (node.optionType) instrument.optionType = OPTION_TYPE_MAP[node.optionType]
      return {
        optionIv: {
          instrument,
          source: OPTION_IV_SOURCE_MAP[node.source],
          operator: OPERATOR_MAP[node.op],
          valuePercent: decimal(node.valuePercent),
        },
      }
    }

    case 'schedule':
      return {
        schedule:
          node.variant === 'fire_at'
            ? { fireAtMs: decimal(node.value) }
            : { delaySeconds: decimal(node.value) },
      }

    case 'db_news': {
      const dbNews: NonNullable<Condition['dbNews']> = {
        eeType: cleanChips(node.eeType),
        newsType: cleanChips(node.newsType),
        coins: cleanChips(node.coins),
        tags: cleanChips(node.tags),
        textContains: cleanChips(node.textContains),
      }
      if (node.eventNotBeforeMs) dbNews.eventNotBeforeMs = node.eventNotBeforeMs
      return { dbNews }
    }

    case 'action_event':
      return {
        actionEvent: {
          eventType: ACTION_EVENT_TYPE_MAP[node.eventType],
          target: {
            orderId: node.targetType === 'order_id' ? node.targetValue : undefined,
            algoId: node.targetType === 'algo_id' ? node.targetValue : undefined,
            symbol: node.targetType === 'symbol' ? node.targetValue : undefined,
          },
          partialFillThresholdPercent:
            node.eventType === 'partially_filled' && node.partialFillThreshold
              ? decimal(node.partialFillThreshold)
              : undefined,
        },
      }

    case 'and':
      return { andCondition: { conditions: node.children.map((c) => buildCondition(c, ctx)) } }

    case 'or':
      return { orCondition: { conditions: node.children.map((c) => buildCondition(c, ctx)) } }
  }
}
